// no-dd-sa:typescript-best-practices/boolean-prop-naming
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getPageMeta } from '@/lib/seoConfig';

const BASE_URL = 'https://codepulsehq.com';

interface SEOProps {
  /** Override the default title */
  title?: string;
  /** Override the default description */
  description?: string;
  /** Force noindex regardless of config */
  noindex?: boolean;
  /** Structured data (JSON-LD) to inject into page head */
  structuredData?: object | object[];
  /** Custom Open Graph image URL (relative or absolute) */
  ogImage?: string;
  /** Meta keywords for SEO (comma-separated or array) */
  keywords?: string | string[];
  /** Explicit canonical path override (e.g. "/guides/my-guide"). Takes precedence over auto-detected pathname. */
  canonicalPath?: string;
}

/**
 * SEO component for managing page metadata.
 *
 * Uses centralized config from seoConfig.ts, with optional overrides.
 * Automatically handles:
 * - Page title
 * - Meta description
 * - Canonical URL
 * - Open Graph tags
 * - Twitter tags
 * - noindex for protected pages
 *
 * @example
 * // Use config defaults
 * <SEO />
 *
 * @example
 * // Override with dynamic data
 * <SEO title={`${developer.name} | CodePulse`} />
 */
export function SEO({ title, description, noindex, structuredData, ogImage, keywords, canonicalPath }: SEOProps) {
  const { pathname } = useLocation();
  const meta = getPageMeta(pathname);

  const finalTitle = title || meta.title;
  const finalDescription = description || meta.description;
  const shouldNoindex = noindex ?? meta.noindex;
  const finalKeywords = keywords
    ? Array.isArray(keywords)
      ? keywords.join(', ')
      : keywords
    : null;
  // Canonical URL: explicit override > auto-detected pathname.
  // Always strip trailing slash (except root "/") and never include query params or fragments.
  const basePath = canonicalPath ?? pathname;
  const normalizedPathname = basePath === '/' ? basePath : basePath.replace(/\/$/, '');
  const canonicalUrl = `${BASE_URL}${normalizedPathname}`;

  // Default OG image, can be overridden per-page
  const defaultOgImage = `${BASE_URL}/og-image.png`;
  const finalOgImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`)
    : defaultOgImage;

  // Normalize structured data to array for consistent JSON-LD output
  const jsonLdData = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      {shouldNoindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:site_name" content="CodePulse" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalOgImage} />
      <meta name="twitter:creator" content="@codepulsehq" />

      {/* Structured Data */}
      {jsonLdData &&
        jsonLdData.map((data, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))}
    </Helmet>
  );
}

export default SEO;